import {
  api,
  GetApiV1GroupsApiArg,
  Group,
  fetchInterceptor,
} from '@wellesley/openapi'
import { handleBasicResponse } from '@wellesley/openapi/helpers'
import { groupSlice } from '../slices/useGroupSlice'
import { toast } from '@wellesley/system-toast'
import { getErrorMsg } from '@wellesley/error-kit'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import {
  setGroupData,
  setGroupLoading,
} from '@wellesley/host-remote/api/actions'

const mockLocations = ['New York', 'San Francisco', 'Los Angeles', 'Chicago']

const groupsApi = api.enhanceEndpoints({
  endpoints: {
    postApiV1Groups: {
      invalidatesTags: ['Groups', 'USER_CHANGED'],
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    getApiV1Groups: {
      providesTags: ['Groups', 'USER_CHANGED'],
      async onCacheEntryAdded(
        _,
        { dispatch, cacheDataLoaded, cacheEntryRemoved },
      ) {
        await cacheDataLoaded
        const off = fetchInterceptor.on({
          url: '/api/v1/groups',
          method: 'POST',
          callback: () => dispatch(api.util.invalidateTags(['Groups'])),
        })
        await cacheEntryRemoved
        off()
      },
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    getApiV1GroupsMyGroups: {
      providesTags: ['Groups', 'USER_CHANGED'],
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    getApiV1GroupsSearch: {
      providesTags: ['Groups', 'USER_CHANGED'],
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    deleteApiV1GroupsByGroupId: {
      invalidatesTags: ['Groups', 'USER_CHANGED'],
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    postApiV1GroupsByGroupIdJoin: {
      invalidatesTags: (_result, _error, { groupId }) => [
        { type: 'GroupMembership', id: groupId || undefined },
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    deleteApiV1GroupsByGroupIdJoin: {
      invalidatesTags: (_result, _error, { groupId }) => [
        { type: 'GroupMembership', id: groupId || undefined },
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    getApiV1GroupsNameByGroupname: {
      extraOptions: {
        initialDataKey: 'group',
      },
      providesTags: (result: Group | undefined, _error, { groupname }) => [
        { type: 'Groups', id: result?.groupId || groupname },
        'USER_CHANGED',
      ],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        dispatch(setGroupLoading([args.groupname, true]))
        try {
          const res = await queryFulfilled
          if (res) {
            dispatch(setGroupData([args.groupname, res.data]))
            dispatch(groupSlice.actions.setDescription(res.data.summary))

            // dispatch(groupSlice.actions.setLinks(mapFieldsToLinks(mockFields)))

            // TODO: set real data from backend and remove mocks
            dispatch(groupSlice.actions.setLocations(mockLocations))
          }
        } catch (e) {
          const error = e as { error: FetchBaseQueryError }
          const errorData = error?.error?.data as { errorCode?: string }
          const isNotFound = errorData?.errorCode === 'ResourceNotFound'

          //TODO: split not found and private group errors
          if (!isNotFound) {
            toast.error(
              `Failed to fetch group name: ${getErrorMsg(error.error)}`,
            )
          }
        } finally {
          dispatch(setGroupLoading([args.groupname, false]))
        }
      },
    },
    getApiV1GroupsByGroupId: {
      providesTags: ['Groups', 'USER_CHANGED'],
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    putApiV1GroupsByGroupIdPin: {
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        const state = getState() as unknown as {
          openapi: { queries: Record<string, unknown> }
        }
        const queries = state.openapi?.queries || {}

        const patchResults: { undo: () => void }[] = []

        for (const key of Object.keys(queries)) {
          if (key.startsWith('getApiV1GroupsMyGroups(')) {
            const queryData = queries[key] as { data?: Group[] } | undefined
            if (queryData?.data?.some((g) => g.groupId === args.groupId)) {
              const queryArg = JSON.parse(
                key.replace('getApiV1GroupsMyGroups(', '').slice(0, -1),
              ) as GetApiV1GroupsApiArg
              patchResults.push(
                dispatch(
                  groupsApi.util.updateQueryData(
                    'getApiV1GroupsMyGroups',
                    queryArg,
                    (draft) => {
                      const group = draft.find(
                        (g) => g.groupId === args.groupId,
                      )
                      if (group) {
                        group.pin = 1
                      }
                    },
                  ),
                ),
              )
            }
          }
        }

        try {
          await handleBasicResponse(queryFulfilled)
        } catch {
          patchResults.forEach((patch) => patch.undo())
        }
      },
    },
    deleteApiV1GroupsByGroupIdPin: {
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        const state = getState() as unknown as {
          openapi: { queries: Record<string, unknown> }
        }
        const queries = state.openapi?.queries || {}

        const patchResults: { undo: () => void }[] = []

        for (const key of Object.keys(queries)) {
          if (key.startsWith('getApiV1GroupsMyGroups(')) {
            const queryData = queries[key] as { data?: Group[] } | undefined
            if (queryData?.data?.some((g) => g.groupId === args.groupId)) {
              const queryArg = JSON.parse(
                key.replace('getApiV1GroupsMyGroups(', '').slice(0, -1),
              ) as GetApiV1GroupsApiArg
              patchResults.push(
                dispatch(
                  groupsApi.util.updateQueryData(
                    'getApiV1GroupsMyGroups',
                    queryArg,
                    (draft) => {
                      const group = draft.find(
                        (g) => g.groupId === args.groupId,
                      )
                      if (group) {
                        group.pin = 0
                      }
                    },
                  ),
                ),
              )
            }
          }
        }

        try {
          await handleBasicResponse(queryFulfilled)
        } catch {
          patchResults.forEach((patch) => patch.undo())
        }
      },
    },
    putApiV1GroupsByGroupIdBranding: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    putApiV1GroupsByGroupId: {
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        const state = getState() as unknown as {
          openapi: { queries: Record<string, unknown> }
        }
        const queries = state.openapi?.queries || {}

        let groupname: string | undefined
        for (const key of Object.keys(queries)) {
          if (key.startsWith('getApiV1GroupsNameByGroupname')) {
            const queryData = queries[key] as { data?: Group } | undefined
            if (queryData?.data?.groupId === args.groupId) {
              groupname = queryData.data.groupName
              break
            }
          }
        }

        if (!groupname) return

        const patchResult = dispatch(
          groupsApi.util.updateQueryData(
            'getApiV1GroupsNameByGroupname',
            { groupname },
            (draft) => {
              if (args.groupUpdate.fields) {
                draft.fields = args.groupUpdate.fields
              }
              if (args.groupUpdate.name) {
                draft.groupName = args.groupUpdate.name
              }
              if (args.groupUpdate.displayName) {
                draft.displayName = args.groupUpdate.displayName
              }
              if (args.groupUpdate.description) {
                draft.summary = args.groupUpdate.description
              }
            },
          ),
        )

        try {
          const res = await handleBasicResponse(queryFulfilled)
          if (res?.data) {
            dispatch(setGroupData([groupname, res.data]))
          }
        } catch {
          patchResult.undo()
        }
      },
    },
    getApiV1Data: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
  },
})

export const {
  useGetApiV1DataQuery,
  usePostApiV1GroupsMutation,
  useGetApiV1GroupsQuery,
  useLazyGetApiV1GroupsQuery,
  useGetApiV1GroupsMyGroupsQuery,
  useLazyGetApiV1GroupsMyGroupsQuery,
  useDeleteApiV1GroupsByGroupIdMutation,
  useGetApiV1GroupsSearchQuery,
  useLazyGetApiV1GroupsSearchQuery,
  useGetApiV1GroupsByGroupIdQuery,
  useDeleteApiV1GroupsByGroupIdJoinMutation,
  usePostApiV1GroupsByGroupIdJoinMutation,
  useGetApiV1GroupsNameByGroupnameQuery,
  usePutApiV1GroupsByGroupIdBrandingMutation,
  useDeleteApiV1GroupsByGroupIdPinMutation,
  usePutApiV1GroupsByGroupIdPinMutation,
} = groupsApi
